import { useHarvesterAddress } from './useHarvesterAddress';
import { useNebyStakerAddress } from './useNebyStakerAddress';
import { useStakerAddress } from './useStakerAddress';
import type { Contract } from '@ethersproject/contracts';
import * as sapphire from '@oasisprotocol/sapphire-paratime';
import {
  ChainId,
  ENS_REGISTRAR_ADDRESSES,
  MULTICALL_ADDRESSES,
  NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  V3_MIGRATOR_ADDRESSES
} from '@uniswap/sdk-core';
import NonfungiblePositionManagerJson from '@uniswap/v3-periphery/artifacts/contracts/NonfungiblePositionManager.sol/NonfungiblePositionManager.json';
import V3MigratorJson from '@uniswap/v3-periphery/artifacts/contracts/V3Migrator.sol/V3Migrator.json';
import UniswapInterfaceMulticallJson from '@uniswap/v3-periphery/artifacts/contracts/lens/UniswapInterfaceMulticall.sol/UniswapInterfaceMulticall.json';
import BRIDGED_PURCHASE_SOURCE_ABI from 'abis/bridged-purchase-source.json';
import EIP_2612 from 'abis/eip_2612.json';
import ENS_ABI from 'abis/ens-registrar.json';
import ERC20_ABI from 'abis/erc20.json';
import ERC20_BYTES32_ABI from 'abis/erc20_bytes32.json';
import ERC721_ABI from 'abis/erc721.json';
import ERC1155_ABI from 'abis/erc1155.json';
import HARVESTER_ABI from 'abis/harvester.json';
import NEBY_STAKER_ABI from 'abis/neby-staker.json';
import SALE_ABI from 'abis/sale.json';
import STAKER_ABI from 'abis/staker.json';
import type {
  BridgedPurchaseSource,
  EnsPublicResolver,
  EnsRegistrar,
  Erc20,
  Erc721,
  Erc1155,
  NebyStaker,
  Sale,
  Staker,
  Weth
} from 'abis/types';
import type { Harvester } from 'abis/types';
import WETH_ABI from 'abis/weth.json';
import { DEPRECATED_RPC_PROVIDERS } from 'constants/providers';
import { WRAPPED_NATIVE_CURRENCY, isOasisNetwork } from 'constants/tokens';
import { useMemo } from 'react';
import type {
  NonfungiblePositionManager,
  UniswapInterfaceMulticall
} from 'types/v3';
import type { V3Migrator } from 'types/v3/V3Migrator';

import ENS_PUBLIC_RESOLVER_ABI from 'abis/ens-public-resolver.json';

import { getContract } from 'utils';

import { useWeb3React } from 'hooks/useWeb3React';

import IUniswapV2PairJson from '@uniswap/v2-core/build/IUniswapV2Pair.json';

const { abi: IUniswapV2PairABI } = IUniswapV2PairJson;
const { abi: MulticallABI } = UniswapInterfaceMulticallJson;
const { abi: NFTPositionManagerABI } = NonfungiblePositionManagerJson;
const { abi: V2MigratorABI } = V3MigratorJson;

// returns null on errors
export function useContract<T extends Contract = Contract>(
  addressOrAddressMap: string | { [chainId: number]: string } | undefined,
  ABI: any,
  withSignerIfPossible = true
): T | null {
  const { provider, account, chainId } = useWeb3React();

  return useMemo(() => {
    if (!addressOrAddressMap || !ABI || !provider || !chainId) return null;
    let address: string | undefined;
    if (typeof addressOrAddressMap === 'string') address = addressOrAddressMap;
    else address = addressOrAddressMap[chainId];
    if (!address) return null;
    try {
      return getContract(
        address,
        ABI,
        provider,
        withSignerIfPossible && account ? account : undefined
      );
    } catch (error) {
      console.error('Failed to get contract', error);
      return null;
    }
  }, [
    addressOrAddressMap,
    ABI,
    provider,
    chainId,
    withSignerIfPossible,
    account
  ]) as T;
}

function useMainnetContract<T extends Contract = Contract>(
  address: string | undefined,
  ABI: any
): T | null {
  const { chainId } = useWeb3React();
  const isMainnet = chainId === ChainId.MAINNET;
  const contract = useContract(isMainnet ? address : undefined, ABI, false);
  const providers = DEPRECATED_RPC_PROVIDERS;

  return useMemo(() => {
    if (isMainnet) return contract;
    if (!address) return null;
    const provider = providers[ChainId.MAINNET];
    try {
      return getContract(address, ABI, provider);
    } catch (error) {
      console.error('Failed to get mainnet contract', error);
      return null;
    }
  }, [isMainnet, contract, address, providers, ABI]) as T;
}

export function useV2MigratorContract() {
  return useContract<V3Migrator>(V3_MIGRATOR_ADDRESSES, V2MigratorABI, true);
}

export function useTokenContract(
  tokenAddress?: string,
  withSignerIfPossible?: boolean
) {
  const { chainId, provider } = useWeb3React();
  const contract = useContract<Erc20>(
    tokenAddress,
    ERC20_ABI,
    withSignerIfPossible
  );
  if (chainId && isOasisNetwork(chainId) && provider) {
    return contract?.connect(sapphire.wrap(provider.getSigner()));
  }
  return contract;
}

export function useWETHContract(withSignerIfPossible?: boolean) {
  const { chainId, provider } = useWeb3React();
  const contract = useContract<Weth>(
    chainId ? WRAPPED_NATIVE_CURRENCY[chainId]?.address : undefined,
    WETH_ABI,
    withSignerIfPossible
  );
  if (chainId && isOasisNetwork(chainId) && provider) {
    return contract?.connect(sapphire.wrap(provider.getSigner()));
  }
  return contract;
}

export function useERC721Contract(nftAddress?: string) {
  return useContract<Erc721>(nftAddress, ERC721_ABI, false);
}

export function useERC1155Contract(nftAddress?: string) {
  return useContract<Erc1155>(nftAddress, ERC1155_ABI, false);
}

export function useENSRegistrarContract() {
  return useMainnetContract<EnsRegistrar>(
    ENS_REGISTRAR_ADDRESSES[ChainId.MAINNET],
    ENS_ABI
  );
}

export function useENSResolverContract(address: string | undefined) {
  return useMainnetContract<EnsPublicResolver>(
    address,
    ENS_PUBLIC_RESOLVER_ABI
  );
}

export function useBytes32TokenContract(
  tokenAddress?: string,
  withSignerIfPossible?: boolean
): Contract | null {
  return useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible);
}

export function useEIP2612Contract(tokenAddress?: string): Contract | null {
  return useContract(tokenAddress, EIP_2612, false);
}

export function usePairContract(
  pairAddress?: string,
  withSignerIfPossible?: boolean
): Contract | null {
  return useContract(pairAddress, IUniswapV2PairABI, withSignerIfPossible);
}

export function useInterfaceMulticall() {
  return useContract<UniswapInterfaceMulticall>(
    MULTICALL_ADDRESSES,
    MulticallABI,
    false
  ) as UniswapInterfaceMulticall;
}

export function useMainnetInterfaceMulticall() {
  return useMainnetContract<UniswapInterfaceMulticall>(
    MULTICALL_ADDRESSES[ChainId.MAINNET],
    MulticallABI
  ) as UniswapInterfaceMulticall;
}

export function useV3NFTPositionManagerContract(
  withSignerIfPossible?: boolean
): NonfungiblePositionManager | null {
  const contract = useContract<NonfungiblePositionManager>(
    NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
    NFTPositionManagerABI,
    withSignerIfPossible
  );

  return contract;
}

export const useHarvesterContract = () => {
  const { provider } = useWeb3React();
  const harvesterAddress = useHarvesterAddress();
  const contract = useContract<Harvester>(harvesterAddress, HARVESTER_ABI);
  if (provider) return contract?.connect(sapphire.wrap(provider));
  return contract;
};

export const useStakerContract = () => {
  const { provider } = useWeb3React();
  const stakerAddress = useStakerAddress();
  const contract = useContract<Staker>(stakerAddress, STAKER_ABI);
  if (provider) return contract?.connect(sapphire.wrap(provider.getSigner()));
  return contract;
};

type useNebyStakerContractProps = {
  mode?: 'read' | 'write';
};

export const useNebyStakerContract = ({
  mode = 'read'
}: useNebyStakerContractProps) => {
  const { provider } = useWeb3React();
  const stakerAddress = useNebyStakerAddress();

  const rawContract = useContract<NebyStaker>(stakerAddress, NEBY_STAKER_ABI);
  const wrappedContract = useMemo(() => {
    if (!rawContract) {
      return null;
    }

    if (!provider) {
      return rawContract;
    }

    return rawContract.connect(sapphire.wrap(provider.getSigner()));
  }, [provider, rawContract]);

  return mode === 'write' ? wrappedContract : rawContract;
};

export const useSaleContract = (address: string | undefined) => {
  const { provider } = useWeb3React();
  const contract = useContract<Sale>(address, SALE_ABI);
  if (provider) return contract?.connect(sapphire.wrap(provider.getSigner()));
  return contract;
};

export const useBridgedPurchaseSourceContract = (address: string | undefined) =>
  useContract<BridgedPurchaseSource>(address, BRIDGED_PURCHASE_SOURCE_ABI);
